import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import {
  AiaG702Summary,
  AiaG703LineItem,
  MoneyValue,
} from "hooks/useProjectAiaSummary";

export interface AiaPdfProps {
  g702?: AiaG702Summary | null;
  g703Rows?: AiaG703LineItem[];
  retainageRate?: string;
  projectName?: string;
  lenderName?: string;
  showG702?: boolean;
  variant?: "standard" | "gc";
}

/* ── Helpers ─────────────────────────────────────────────────────── */

const fmtMoney = (money?: MoneyValue | null): string => {
  if (!money || money.amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: money.currency?.code || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(money.amount ?? 0));
};

const fmtPct = (value?: number | string | null): string => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "0%";
  return `${Number.isInteger(num) ? num : num.toFixed(2)}%`;
};

const amountOf = (m?: MoneyValue | null) => Number(m?.amount ?? 0);

/* ── Styles ──────────────────────────────────────────────────────── */

const S = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 36,
    paddingHorizontal: 30,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#111111",
    backgroundColor: "#ffffff",
  },

  /* Header */
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#dde1e6",
  },
  headerTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 14,
    color: "#14508c",
    marginBottom: 3,
  },
  headerSub: { fontSize: 7.5, color: "#909399", marginTop: 2 },
  headerMetaLine: { fontSize: 8, color: "#555c66", marginBottom: 2 },
  headerRight: { alignItems: "flex-end" },

  /* Card */
  card: {
    borderWidth: 1,
    borderColor: "#dde1e6",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 14,
    backgroundColor: "#ffffff",
  },
  cardTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#dde1e6",
  },
  cardTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#555c66",
    letterSpacing: 0.3,
  },
  retainageChip: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    color: "#14508c",
    letterSpacing: 0.3,
  },

  /* G702 lines */
  g702Line: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#f4f5f6",
  },
  g702LineTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 7,
    paddingHorizontal: 4,
    backgroundColor: "#f0fdf4",
    borderRadius: 4,
    marginTop: 2,
  },
  g702Left: { flexDirection: "row", alignItems: "center", flex: 1 },
  g702Num: { width: 26, fontSize: 8, color: "#909399" },
  g702Label: { fontSize: 8.5, color: "#111111", flex: 1 },
  g702LabelBold: { fontFamily: "Helvetica-Bold", fontSize: 8.5 },
  g702Val: { fontSize: 8.5, color: "#111111", textAlign: "right", width: 110 },
  g702ValBlue: {
    color: "#14508c",
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
    width: 110,
  },
  g702ValGreen: {
    color: "#1a6b35",
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
    width: 110,
    fontSize: 9.5,
  },

  /* G703 table */
  tHead: {
    flexDirection: "row",
    backgroundColor: "#f4f5f6",
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRadius: 3,
    marginBottom: 1,
  },
  tRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 5,
    paddingHorizontal: 6,
  },
  tRowAlt: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 5,
    paddingHorizontal: 6,
    backgroundColor: "#fafafa",
  },
  tTotal: {
    flexDirection: "row",
    backgroundColor: "#edf1f5",
    paddingVertical: 7,
    paddingHorizontal: 6,
    borderRadius: 3,
    marginTop: 2,
  },
  th: { fontFamily: "Helvetica-Bold", fontSize: 7.5, color: "#555c66" },
  td: { fontSize: 8, color: "#111111" },
  tdMuted: { fontSize: 8, color: "#909399" },
  tdBold: { fontFamily: "Helvetica-Bold", fontSize: 8.5 },
  tdGreen: { fontSize: 8, color: "#1a6b35" },
  tdWarn: { fontSize: 8, color: "#b45309" },

  /* G703 column widths */
  cItem: { width: 48 },
  cDesc: { flex: 1, paddingRight: 6 },
  cSub: { width: 80, paddingRight: 6 },
  cMoney: { width: 68, textAlign: "right" },
  cPct: { width: 36, textAlign: "right" },

  /* Footer */
  footer: {
    position: "absolute",
    bottom: 16,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#dde1e6",
    paddingTop: 5,
  },
  footerText: { fontSize: 7, color: "#909399" },
});

/* ── Component ───────────────────────────────────────────────────── */

export default function AiaPdf({
  g702,
  g703Rows = [],
  retainageRate,
  projectName,
  lenderName,
  showG702 = true,
  variant = "standard",
}: AiaPdfProps) {
  const isGcView = variant === "gc";
  const today = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const rate = retainageRate || g702?.retainageRate || "";

  const g702Lines = g702
    ? [
        {
          num: "1",
          label: "Original contract sum",
          val: fmtMoney(g702.originalContractSum),
          color: "normal",
        },
        // {
        //   num: "1a",
        //   label: "SOV update",
        //   val: fmtMoney(g702.sovUpdate),
        //   color: "normal",
        // },
        {
          num: "2",
          label: "Net change by change orders",
          val: fmtMoney(g702.netChangeByChangeOrders),
          color: "normal",
        },
        {
          num: "3",
          label: "Contract sum to date",
          val: fmtMoney(g702.contractSumToDate),
          color: "normal",
        },
        {
          num: "4",
          label: "Total completed & stored to date",
          val: fmtMoney(g702.totalCompletedAndStored),
          color: "normal",
        },
        {
          num: "5a",
          label: `Retainage: ${fmtPct(g702.retainageRate)} of completed work`,
          val: fmtMoney(g702.completedWorkRetainage),
          color: "normal",
        },
        {
          num: "5b",
          label: "Retainage: stored material",
          val: fmtMoney(g702.storedAmountRetainage),
          color: "normal",
        },
        {
          num: "5c",
          label: "Total retainage",
          val: fmtMoney(g702.totalRetainage),
          color: "normal",
        },
        {
          num: "6",
          label: "Total earned less retainage",
          val: fmtMoney(g702.totalEarnedLessRetainage),
          color: "blue",
        },
        {
          num: "7",
          label: "Less previous certificates for payment",
          val: fmtMoney(g702.lessPreviousCertificates),
          color: "normal",
        },
        {
          num: "8",
          label: "Current payment due",
          val: fmtMoney(g702.currentPaymentDue),
          color: "green",
          total: true,
        },
        {
          num: "9",
          label: "Balance to finish, including retainage",
          val: fmtMoney(g702.balanceToFinish),
          color: "normal",
        },
      ]
    : [];

  /* G703 totals */
  const totals = g703Rows.reduce(
    (acc, row) => ({
      sched: acc.sched + amountOf(row.sovValue),
      prev: acc.prev + amountOf(row.previousBilled),
      curr: acc.curr + amountOf(row.currentApplication),
      stored: acc.stored + amountOf(row.currentStoredMaterial),
      total: acc.total + amountOf(row.totalCompletedAndStored),
      remain: acc.remain + amountOf(row.balanceToFinish),
      retainage: acc.retainage + amountOf(row.retainageAmount),
    }),
    {
      sched: 0,
      prev: 0,
      curr: 0,
      stored: 0,
      total: 0,
      remain: 0,
      retainage: 0,
    },
  );
  const currency =
    g703Rows[0]?.sovValue?.currency ?? g702?.contractSumToDate?.currency;
  const wrapMoney = (amount: number) => ({ amount, currency });
  const totalPct = totals.sched > 0 ? (totals.total / totals.sched) * 100 : 0;

  const pctColor = (pct?: number | null) => {
    const n = Number(pct ?? 0);
    if (n === 100) return S.tdGreen;
    if (n === 0) return S.tdMuted;
    if (n < 30) return S.tdWarn;
    return S.td;
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={S.page}>
        {/* ── Header ── */}
        <View style={S.headerRow}>
          <View>
            <Text style={S.headerTitle}>
              {showG702
                ? "AIA G702 / G703 — Application & Certificate for Payment"
                : "AIA G703 — Continuation Sheet"}
            </Text>
            <Text style={S.headerSub}>
              Prepared by CSC New England · Read-only
              {lenderName ? ` · Lender: ${lenderName}` : ""}
            </Text>
          </View>
          <View style={S.headerRight}>
            {projectName ? (
              <Text
                style={[
                  S.headerMetaLine,
                  { fontFamily: "Helvetica-Bold", color: "#111" },
                ]}
              >
                {projectName}
              </Text>
            ) : null}
            <Text style={S.headerMetaLine}>Generated: {today}</Text>
          </View>
        </View>

        {/* ── G702 Card ── */}
        {showG702 && (
          <View style={S.card}>
            <View style={S.cardTitleRow}>
              <Text style={S.cardTitle}>
                G702 — APPLICATION &amp; CERTIFICATE FOR PAYMENT
              </Text>
              {rate ? (
                <Text style={S.retainageChip}>
                  RETAINAGE RATE: {fmtPct(rate)}
                </Text>
              ) : null}
            </View>

            {g702Lines.map((line) => (
              <View
                key={line.num}
                style={line.total ? S.g702LineTotal : S.g702Line}
                wrap={false}
              >
                <View style={S.g702Left}>
                  <Text style={S.g702Num}>{line.num}</Text>
                  <Text style={line.total ? S.g702LabelBold : S.g702Label}>
                    {line.label}
                  </Text>
                </View>
                <Text
                  style={
                    line.color === "green"
                      ? S.g702ValGreen
                      : line.color === "blue"
                        ? S.g702ValBlue
                        : S.g702Val
                  }
                >
                  {line.val}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* ── G703 Card ── */}
        <View style={S.card}>
          <View style={S.cardTitleRow}>
            <Text style={S.cardTitle}>
              G703 — CONTINUATION SHEET (SCHEDULE OF VALUES)
            </Text>
          </View>

          {/* Table header */}
          <View style={S.tHead}>
            <Text style={[S.th, S.cItem]}>Item</Text>
            <Text style={[S.th, S.cDesc]}>Description</Text>
            {isGcView && (
              <Text style={[S.th, S.cSub]}>Subcontractor</Text>
            )}
            <Text style={[S.th, S.cMoney]}>Scheduled</Text>
            <Text style={[S.th, S.cMoney]}>Prev. App.</Text>
            <Text style={[S.th, S.cMoney]}>This Period</Text>
            <Text style={[S.th, S.cMoney]}>Materials</Text>
            <Text style={[S.th, S.cMoney]}>Total</Text>
            <Text style={[S.th, S.cPct]}>%</Text>
            <Text style={[S.th, S.cMoney]}>Balance</Text>
            {!isGcView && (
              <Text style={[S.th, S.cMoney]}>Retainage</Text>
            )}
          </View>

          {/* Data rows */}
          {g703Rows.map((row, i) => (
            <View
              key={row.aiaItemId ?? row.aiaItemCode ?? i}
              style={i % 2 === 0 ? S.tRow : S.tRowAlt}
              wrap={false}
            >
              <Text style={[S.tdMuted, S.cItem]}>
                {row.itemNo ??
                  (row.aiaItem?.itemLabel ||
                    String(row.aiaItemCode || i + 1))}
              </Text>
              <Text style={[S.td, S.cDesc]}>
                {row.description || row.aiaItem?.itemName || "—"}
              </Text>
              {isGcView && (
                <Text style={[S.td, S.cSub]}>
                  {row.subContractor?.companyName || "—"}
                </Text>
              )}
              <Text style={[S.td, S.cMoney]}>{fmtMoney(row.sovValue)}</Text>
              <Text style={[S.td, S.cMoney]}>
                {fmtMoney(row.previousBilled)}
              </Text>
              <Text style={[S.td, S.cMoney]}>
                {fmtMoney(row.currentApplication)}
              </Text>
              <Text style={[S.td, S.cMoney]}>
                {fmtMoney(row.currentStoredMaterial)}
              </Text>
              <Text style={[S.td, S.cMoney]}>
                {fmtMoney(row.totalCompletedAndStored)}
              </Text>
              <Text style={[pctColor(row.percentageComplete), S.cPct]}>
                {fmtPct(row.percentageComplete)}
              </Text>
              <Text style={[S.td, S.cMoney]}>
                {fmtMoney(row.balanceToFinish)}
              </Text>
              {!isGcView && (
                <Text style={[S.td, S.cMoney]}>
                  {fmtMoney(row.retainageAmount)}
                </Text>
              )}
            </View>
          ))}

          {/* Total row */}
          {g703Rows.length > 0 && (
            <View style={S.tTotal} wrap={false}>
              <Text style={[S.tdBold, S.cItem]}>TOTAL</Text>
              <Text style={[S.tdBold, S.cDesc]} />
              {isGcView && <Text style={[S.tdBold, S.cSub]} />}
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(totals.sched))}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(totals.prev))}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(totals.curr))}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(totals.stored))}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(totals.total))}
              </Text>
              <Text style={[pctColor(totalPct), S.tdBold, S.cPct]}>
                {fmtPct(totalPct)}
              </Text>
              <Text style={[S.tdBold, S.cMoney]}>
                {fmtMoney(wrapMoney(totals.remain))}
              </Text>
              {!isGcView && (
                <Text style={[S.tdBold, S.cMoney]}>
                  {fmtMoney(wrapMoney(totals.retainage))}
                </Text>
              )}
            </View>
          )}
        </View>

        {/* Footer */}
        <View style={S.footer} fixed>
          <Text style={S.footerText}>
            CSC New England · AIA G702 / G703 · {today}
          </Text>
          <Text
            style={S.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
